<template>
  <v-container fluid class="create-user">
    <v-row class="flex-grow-0">
      <v-btn text x-small color="primary" exact :to="{ name: 'user-list' }">
        &lt;&lt; {{ $t("app_drawer.users") }}
      </v-btn>
    </v-row>
    <v-row>
      <v-col>
        <v-card>
          <v-card-title>
            {{ $t("user.create_title") }}

            <v-switch
              class="ml-4"
              v-model="user.is_external"
              :label="$t('user.is_external')"
            ></v-switch>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <v-form ref="form">
              <v-text-field
                outlined
                :label="
                  user.is_external ? $t('user.email') : $t('user.username')
                "
                v-model="user.username"
                :rules="user.is_external ? emailRules : []"
              ></v-text-field>
              <v-text-field
                outlined
                :label="$t('user.name')"
                v-model="user.name"
                :rules="requiredRules"
              ></v-text-field>
              <v-text-field
                outlined
                :label="$t('user.company')"
                v-model="user.company"
                :rules="requiredRules"
              ></v-text-field>
              <v-text-field
                outlined
                :label="$t('user.post')"
                v-model="user.post"
                :rules="requiredRules"
                v-if="!user.is_external"
              ></v-text-field>
              <v-text-field
                outlined
                :label="$t('user.phone')"
                v-model="user.phone"
                prefix="+852"
              ></v-text-field>
              <v-divider></v-divider>
              <h3 class="mt-4">{{ $t("user.permissions") }}</h3>
              <v-row>
                <div
                  class="mx-4"
                  style="
                    display: flex;
                    flex-direction: row;
                    flex-wrap: wrap;
                    gap: 1rem;
                  "
                >
                  <v-checkbox
                    v-model="isAdmin"
                    :label="$t('user.is_admin')"
                  ></v-checkbox>
                  <v-checkbox
                    v-model="permissions"
                    value="esor.manage_sor"
                    :disabled="isAdmin"
                    :label="$t('user.manage_sor')"
                  ></v-checkbox>
                  <v-checkbox
                    v-model="permissions"
                    value="esor.input_sor"
                    :disabled="isAdmin"
                    :label="$t('user.input_sor')"
                  ></v-checkbox>
                  <v-checkbox
                    v-model="permissions"
                    :disabled="isAdmin"
                    value="esor.create_contract"
                    :label="$t('user.create_contract')"
                  ></v-checkbox>

                  <v-checkbox
                    v-model="permissions"
                    :disabled="isAdmin"
                    value="esor.manage_nsr"
                    :label="$t('user.manage_nsr')"
                  ></v-checkbox>

                  <v-checkbox
                    v-model="permissions"
                    :disabled="isAdmin"
                    value="esor.manage_nsr_ind"
                    :label="$t('user.manage_nsr_ind')"
                  ></v-checkbox>

                  <v-checkbox
                    v-if="canEditRBUPermission"
                    v-model="permissions"
                    value="rbu.manage_dataset"
                    :label="'Manage Reference DB'"
                  ></v-checkbox>
                  <v-checkbox
                    v-if="canEditRBUPermission"
                    v-model="permissions"
                    value="rbu.manage_edition"
                    :label="'Manage RBU Edition'"
                  ></v-checkbox>
                  <v-checkbox
                    v-if="canEditRBUPermission"
                    v-model="permissions"
                    value="rbu.template_verification"
                    :label="$t('permissions.rbu.template_verification')"
                  ></v-checkbox>
                </div>
              </v-row>

              <h3 v-if="canEditRBUPermission" class="mt-4">
                Special Permission
              </h3>
              <v-row v-if="canEditRBUPermission">
                <div
                  class="mx-4"
                  style="
                    display: flex;
                    flex-direction: row;
                    flex-wrap: wrap;
                    gap: 1rem;
                  "
                >
                  <v-checkbox
                    v-model="special_permissions"
                    :value="SPECIAL_PERMISSIONS.ASOR_RBU"
                    :label="'ASoR RBU'"
                  ></v-checkbox>
                </div>
              </v-row>
              <v-row v-if="user.is_external">
                <v-col>
                  <v-divider class="mb-4"></v-divider>
                  <v-text-field
                    outlined
                    :label="$t('signup.password')"
                    v-model="user.password"
                    :type="showSignupPassword ? 'text' : 'password'"
                    :append-icon="
                      showSignupPassword
                        ? 'mdi-eye-off-outline'
                        : 'mdi-eye-outline'
                    "
                    @click:append="showSignupPassword = !showSignupPassword"
                    :rules="passwordRules"
                  >
                  </v-text-field>
                  <v-text-field
                    outlined
                    :label="$t('signup.re_enter_password')"
                    v-model="reEnterPassword"
                    :type="showSignupReEnterPassword ? 'text' : 'password'"
                    :append-icon="
                      showSignupReEnterPassword
                        ? 'mdi-eye-off-outline'
                        : 'mdi-eye-outline'
                    "
                    @click:append="
                      showSignupReEnterPassword = !showSignupReEnterPassword
                    "
                    :rules="reEnterPasswordRules"
                  >
                  </v-text-field>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" @click="$refs.form.validate() && save()">
              {{ $t("save") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { ref } from "@vue/composition-api/dist/vue-composition-api";
import { ACTION_TYPES } from "@/store/types";
import { USER_TYPE, PERMISSIONS, SPECIAL_PERMISSIONS } from "@/constants";
import { computed } from "@vue/composition-api";

export default {
  name: "UserCreate",
  setup(props, { root }) {
    const canEditRBUPermission = computed(() => {
      return (
        root.$store.getters.account &&
        (root.$store.getters.account.type === "SUPER_ADMIN" ||
          root.$store.getters.hasPermission(
            PERMISSIONS.MANAGE_RBU_EDITION,
            true
          ))
      );
    });
    const requiredRules = ref([
      (value) => !!value || root.$t("rules.required"),
    ]);
    // const emailReg =
    //   /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    const emailReg = /^[\w-.+]+@([\w-]+\.)+[\w-]{2,4}$/;
    const emailRules = ref([
      (value) => emailReg.test(value) || "Invalid Email Address",
    ]);
    const phoneRules = ref([
      (value) =>
        /^[2456789][0-9]{7}$/.test(value) || "Invalid Telephone Number",
    ]);
    const passwordRules = ref([
      (value) =>
        (!!value && value.length >= 8) ||
        root.$t("signup.password_contain_eight_characters"),
      (value) =>
        (!!value && !!value.match(/[A-Za-z]/g)) ||
        root.$t("signup.password_contain_letters"),
      (value) =>
        (!!value && !!value.match(/[0-9]/g)) ||
        root.$t("signup.password_contain_numbers"),
      (value) =>
        (!!value &&
          !!value.match(/[!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~]/g) &&
          !value.match(/[ ]/g)) ||
        root.$t("signup.password_contain_special_characters"),
    ]);
    const reEnterPasswordRules = ref([
      (value) =>
        (!!value && value === user.value.password) ||
        root.$t("signup.password_mismatch"),
    ]);

    const user = ref({
      is_external: false,
      username: "",
      name: "",
      company: "",
      post: "",
      phone: "",
      password: "",
    });
    const isAdmin = ref(false);
    const permissions = ref([]);
    const special_permissions = ref([]);

    const showSignupPassword = ref(false);
    const reEnterPassword = ref(null);
    const showSignupReEnterPassword = ref(false);

    const save = async function () {
      const response = await root.$store.dispatch(ACTION_TYPES.CALL_API, {
        url: "admin/user/create/",
        params: {
          ...user.value,
          type: isAdmin.value ? USER_TYPE.ADMIN : USER_TYPE.USER,
          permissions: isAdmin.value ? permissions.value : permissions.value,
          special_permissions: special_permissions.value,
        },
      });

      if (response.body.success) {
        root.$showAlertMessage("User Successfully Created");
        root.$router.push({ name: "user-list" });
      }
    };

    return {
      requiredRules,
      emailRules,
      phoneRules,
      passwordRules,
      reEnterPasswordRules,
      user,
      isAdmin,
      permissions,
      showSignupPassword,
      reEnterPassword,
      showSignupReEnterPassword,
      save,
      canEditRBUPermission,
      special_permissions,
      SPECIAL_PERMISSIONS,
    };
  },
};
</script>

<style lang="less">
.create-user {
  height: 100%;
  display: flex;
  flex-direction: column;
}
</style>
